import React, { useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import Moment from 'moment';
import { extendMoment } from 'moment-range';
import isEmpty from 'lodash.isempty';
import Classnames from 'classnames';
import TimeIcon from '@bluevalet/react-icons/time';
import { Fixed, InputSelect, Modal } from '@bluevalet/common-ui';

import { getAvailableTimesFromSlots, getAvailableSlot, isErrorField, isObjectEmpty, Slot } from '../utils';
import { persistCart } from '../actions/cart';
import { setArrivalSlotAvailable, setArrivalSlotUnAvailable } from '../actions/slot';

import { TripDetail, TripStatus } from '../../booking/model';
import { State } from '../../booking/reducers';
import { Api } from '../../common/model';

const moment = extendMoment(Moment);

interface Props {
  date: Date | undefined;
  hour: string | undefined;
  slots: Api.SiteSlots | undefined;
  forceValidation?: boolean;
  onHourChange: (value: string) => void;
  tripDetail: TripDetail | undefined;
}

const EndStationTripHourPicker: React.FC<Props> = ({
  date,
  hour,
  slots,
  forceValidation,
  onHourChange,
  tripDetail,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const unavailableModalRef = useRef<any>(null);

  const departure = useSelector((state: State) => state.booking.departure);
  const isArrivalSlotAvailable = useSelector((state: State) => state.booking.isArrivalSlotAvailable);
  const trip = useSelector((state: State) => state.trip.end);

  const prevTripDetailRef = useRef<TripDetail | undefined>();

  const openModal = () => unavailableModalRef.current?.openModal();

  const closeModal = () => unavailableModalRef.current?.closeModal();

  const getDisplayedSlots = useCallback(
    (slots: Api.SiteSlots, withPrevious: boolean): Array<Slot> => {
      const sameDateForArrival = moment(departure.date).isSame(date, 'day');

      const getStartTime = (): string => {
        if (isEmpty(tripDetail)) {
          return sameDateForArrival && departure.hour
            ? moment(departure.hour, 'HH:mm').add(slots.step, 'minutes').format('HH:mm')
            : slots.startTime;
        } else {
          const value = moment(tripDetail.arrivalTime, 'DD/MM/YYYY HH:mm');
          if (withPrevious) value.subtract(slots.step, 'minutes');

          return value.format('HH:mm');
        }
      };

      const startMoment = moment(getStartTime(), 'HH:mm');
      const endMoment = moment(slots.endTime, 'HH:mm');

      return getAvailableTimesFromSlots(slots, startMoment, endMoment);
    },
    [date, tripDetail, departure],
  );

  useEffect(() => {
    if (tripDetail && !isObjectEmpty(tripDetail)) {
      const slotsData = slots ? getDisplayedSlots(slots, true) : [];
      const findSlot = getAvailableSlot(tripDetail, slotsData);
      if (tripDetail !== prevTripDetailRef.current) {
        if (!isEmpty(findSlot) && !findSlot.isUnavailable) {
          onHourChange(findSlot.value);
          dispatch(setArrivalSlotAvailable());
          dispatch(persistCart());
        } else {
          dispatch(setArrivalSlotUnAvailable());
          openModal();
        }
      } else if (
        (trip.status === TripStatus.Unknown ||
          trip.status === TripStatus.Forced ||
          (trip.status === TripStatus.Found && slotsData.length)) &&
        !isArrivalSlotAvailable
      ) {
        dispatch(setArrivalSlotAvailable());
      }
    }

    prevTripDetailRef.current = tripDetail;
  }, [date, hour, trip, tripDetail, isArrivalSlotAvailable, dispatch, slots, onHourChange, getDisplayedSlots]);

  const times = slots ? getDisplayedSlots(slots, false) : [];

  // Checking selected hour is available
  const unavailableHours = times.filter(t => t.isUnavailable).map(t => t.value);

  const hasAvailableHours = times.filter(t => !t.isUnavailable).length > 0;

  const hourValue = hour ? (unavailableHours.indexOf(hour) === -1 ? hour : null) : null;

  const getErrors = () => {
    const success = hour !== undefined && hour.length > 0 && trip.status === TripStatus.Found;
    const error = isErrorField(forceValidation, hourValue) || !hasAvailableHours;

    if (trip.status === TripStatus.Found) {
      const selectedDate = moment(date);

      if (hour) {
        const [hours, minutes] = hour.split(':');
        selectedDate.set({ hours: parseInt(hours, 10), minutes: parseInt(minutes, 10) });
      }

      const tripDate = moment(tripDetail?.arrivalTime, 'DD/MM/YYYY HH:mm');

      // S'il y a une heure de sélectionnée. alors on vérifie qu'elle est supérieur ou égal à l'heure de départ du voyage sélectionné.
      if (hour && hour.length > 0)
        return selectedDate.isSameOrAfter(tripDate) ? { success: true, error } : { error: true, success };

      if (!hour && tripDetail && tripDetail.arrivalTime) return { error: true, success };
    }

    return { success, error };
  };

  const errors = getErrors();

  const getDisplaySelector = () => {
    if (tripDetail && tripDetail.arrivalTime) {
      const [, tripTime] = tripDetail.arrivalTime.split(' ');
      return unavailableHours.includes(tripTime);
    }
    return false;
  };

  const name = `end-time`;
  const fixedHour = !getDisplaySelector() && hasAvailableHours;
  const clsHourPicker = Classnames('HourPicker', { hidden: trip.status === TripStatus.None });
  const label = t('booking-hour_end_train_label');
  const placeholder = t('hour');

  return (
    <div className={clsHourPicker}>
      {fixedHour && (
        <div className={trip.status === TripStatus.Found ? 'success' : 'regular'}>
          <Fixed label={label} value={hour} />
        </div>
      )}

      {!fixedHour && (
        <>
          <InputSelect
            data-cy={name}
            id={name}
            name={name}
            label={label}
            value={hourValue || ''}
            onChange={onHourChange}
            placeholder={placeholder}
            success={errors.success}
            warning={false}
            error={errors.error}
            message={!hasAvailableHours ? t('booking-no-slot') : undefined}
            icon={<TimeIcon />}>
            {times.map(time => (
              <option key={time.value} value={time.value} disabled={time.isUnavailable}>
                {time.isUnavailable ? `${time.value} (${t('full')})` : time.value}
              </option>
            ))}
          </InputSelect>
          <Modal
            id="ArrivalUnavailableModal"
            ref={unavailableModalRef}
            title={t('booking-unavailable-arrival-title')}
            content={`${t('booking-unavailable-arrival-content')}
              ${!times || times.length === 0 ? `\n${t('booking-unavailable-arrival-content-no-slots')}` : ''}`}
            primaryText={t('booking-unavailable-arrival-button')}
            onClickPrimary={closeModal}
          />
        </>
      )}
    </div>
  );
};

export default EndStationTripHourPicker;
